import { jsPDF } from 'jspdf';
import * as BABYLON from '@babylonjs/core';
import { calculateScaffoldLoad } from './ScaffoldingSystem';
import { addLogoToPdfHeader } from './pdfLogoHelper';

const VIEWS = [
  { name: 'Front View', alpha: Math.PI / 2, beta: Math.PI / 2 },
  { name: 'Back View', alpha: -Math.PI / 2, beta: Math.PI / 2 },
  { name: 'Left View', alpha: Math.PI, beta: Math.PI / 2 },
  { name: 'Right View', alpha: 0, beta: Math.PI / 2 },
  { name: 'Top View', alpha: 0, beta: 0.01 },
  { name: 'Bottom View', alpha: 0, beta: Math.PI - 0.01 },
];

function captureView(scene, engine, target, distance, view) {
  return new Promise((resolve) => {
    const cam = new BABYLON.ArcRotateCamera('reportCam', view.alpha, view.beta, distance, target, scene);
    cam.minZ = 0.1;
    cam.maxZ = 500;
    const prevCam = scene.activeCamera;
    scene.activeCamera = cam;
    let frames = 0;
    const obs = scene.onAfterRenderObservable.add(() => {
      frames++;
      if (frames >= 3) {
        scene.onAfterRenderObservable.remove(obs);
        BABYLON.Tools.CreateScreenshotUsingRenderTarget(engine, cam, { width: 1200, height: 900 }, (data) => {
          scene.activeCamera = prevCam;
          cam.dispose();
          resolve(data);
        });
      }
    });
  });
}

export async function exportScaffoldReport(scene, engine, scaffoldConfig, projectName, userName) {
  const ld = calculateScaffoldLoad(scaffoldConfig);
  const li = scaffoldConfig.loadInputs || {};
  const pW = scaffoldConfig.width || 3;
  const pD = scaffoldConfig.depth || 2;
  const pH = scaffoldConfig.height || 3;

  const pdf = new jsPDF('p', 'mm', 'a4');
  const pw = 210;
  const m = 12; // margin
  const cw = pw - m * 2;
  let y = m;

  const hdr = (text, size) => {
    pdf.setFontSize(size || 11);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(30, 30, 30);
    pdf.text(text, m, y);
    y += 5;
    pdf.setDrawColor(140, 140, 140);
    pdf.line(m, y, pw - m, y);
    y += 4;
  };

  const row = (label, value, color) => {
    pdf.setFontSize(8.5);
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(90, 90, 90);
    pdf.text(label, m + 2, y);
    if (color) pdf.setTextColor(...color);
    else pdf.setTextColor(30, 30, 30);
    pdf.setFont('helvetica', 'bold');
    pdf.text(String(value), m + 85, y);
    y += 4.5;
  };

  const sep = (s) => { y += (s || 2); };

  // ===== PAGE 1: Overview + Structure + Weight =====
  // Header banner
  pdf.setFillColor(25, 25, 30);
  pdf.rect(0, 0, pw, 36, 'F');
  // AV Simulation Tool logo (top-right of header)
  await addLogoToPdfHeader(pdf, { x: pw - m - 26, y: 4, height: 16 });
  pdf.setTextColor(255, 255, 255);
  pdf.setFontSize(18);
  pdf.setFont('helvetica', 'bold');
  pdf.text('Scaffold Engineering Report', m, 18);
  pdf.setFontSize(8);
  pdf.setFont('helvetica', 'normal');
  pdf.text('Generated by AV Simulation Tool', m, 27);
  pdf.setFontSize(7);
  pdf.text(new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }), pw - m - 40, 27);

  y = 42;
  // Overview row (compact inline)
  pdf.setFontSize(8);
  pdf.setTextColor(90, 90, 90);
  pdf.text(`Project: `, m, y);
  pdf.setFont('helvetica', 'bold');
  pdf.setTextColor(30, 30, 30);
  pdf.text(projectName || 'Untitled', m + 18, y);
  pdf.setFont('helvetica', 'normal');
  pdf.setTextColor(90, 90, 90);
  pdf.text(`Prepared by: `, m + 80, y);
  pdf.setFont('helvetica', 'bold');
  pdf.setTextColor(30, 30, 30);
  pdf.text(userName || 'System User', m + 105, y);
  pdf.setFont('helvetica', 'normal');
  pdf.setTextColor(90, 90, 90);
  pdf.text(`Material: `, m + 145, y);
  pdf.setFont('helvetica', 'bold');
  pdf.setTextColor(30, 30, 30);
  pdf.text(ld.materialType === 'steel' ? 'Steel' : 'Aluminium', m + 162, y);
  y += 7;

  // Safety disclaimer (compact)
  pdf.setFillColor(255, 245, 210);
  pdf.roundedRect(m, y, cw, 10, 1.5, 1.5, 'F');
  pdf.setFontSize(6.5);
  pdf.setTextColor(140, 100, 0);
  pdf.setFont('helvetica', 'bold');
  pdf.text('WARNING: ', m + 3, y + 4);
  pdf.setFont('helvetica', 'normal');
  pdf.text('These calculations are approximate. Final approval must be done by a certified structural engineer.', m + 22, y + 4);
  pdf.text(`OSHA Safety Factor: ${ld.oshaFactor}:1 | Leg Efficiency: 0.7 | Side Efficiency: 0.6 | Live Load Margin: 1.1x`, m + 3, y + 8);
  y += 14;

  // Structure details + Components side by side
  hdr('Scaffold Structure');
  const leftX = m + 2;
  const rightX = m + cw / 2 + 5;

  // Left column: dimensions
  pdf.setFontSize(8);
  pdf.setFont('helvetica', 'normal');
  pdf.setTextColor(90, 90, 90);
  const dimY = y;
  pdf.text('Primary:', leftX, y); pdf.setFont('helvetica', 'bold'); pdf.setTextColor(30,30,30); pdf.text(`${pW}W × ${pD}D × ${pH}H m`, leftX + 25, y); y += 4.5;
  pdf.setFont('helvetica', 'normal'); pdf.setTextColor(90,90,90);
  pdf.text('Supports:', leftX, y); pdf.setFont('helvetica', 'bold'); pdf.setTextColor(30,30,30); pdf.text(`${(scaffoldConfig.supportScaffolds || []).length} unit(s)`, leftX + 25, y); y += 4.5;

  const supports = scaffoldConfig.supportScaffolds || [];
  for (let i = 0; i < supports.length; i++) {
    const s = supports[i];
    pdf.setFont('helvetica', 'normal'); pdf.setTextColor(100,100,100);
    pdf.text(`  #${i + 1}: ${s.width||2}W×${s.depth||2}D×${s.height||2}H — ${(s.side||'back').toUpperCase()}`, leftX + 4, y);
    y += 4;
  }

  // Right column: component counts
  let ry = dimY;
  pdf.setFont('helvetica', 'normal'); pdf.setTextColor(90,90,90);
  pdf.text('Standards:', rightX, ry); pdf.setFont('helvetica', 'bold'); pdf.setTextColor(30,30,30); pdf.text(`${ld.components.standards}`, rightX + 30, ry); ry += 4.5;
  pdf.setFont('helvetica', 'normal'); pdf.setTextColor(90,90,90);
  pdf.text('Ledgers X:', rightX, ry); pdf.setFont('helvetica', 'bold'); pdf.setTextColor(30,30,30); pdf.text(`${ld.components.ledgersX}`, rightX + 30, ry); ry += 4.5;
  pdf.setFont('helvetica', 'normal'); pdf.setTextColor(90,90,90);
  pdf.text('Ledgers Z:', rightX, ry); pdf.setFont('helvetica', 'bold'); pdf.setTextColor(30,30,30); pdf.text(`${ld.components.ledgersZ}`, rightX + 30, ry); ry += 4.5;
  pdf.setFont('helvetica', 'normal'); pdf.setTextColor(90,90,90);
  pdf.text('Rosettes:', rightX, ry); pdf.setFont('helvetica', 'bold'); pdf.setTextColor(30,30,30); pdf.text(`${ld.components.rosettes}`, rightX + 30, ry); ry += 4.5;
  pdf.setFont('helvetica', 'normal'); pdf.setTextColor(90,90,90);
  pdf.text('Base Jacks:', rightX, ry); pdf.setFont('helvetica', 'bold'); pdf.setTextColor(30,30,30); pdf.text(`${ld.components.baseJacks}`, rightX + 30, ry); ry += 4.5;
  pdf.setFont('helvetica', 'normal'); pdf.setTextColor(90,90,90);
  pdf.text('Deck Area:', rightX, ry); pdf.setFont('helvetica', 'bold'); pdf.setTextColor(30,30,30); pdf.text(`${ld.components.deckArea} m²`, rightX + 30, ry); ry += 4.5;
  pdf.setFont('helvetica', 'bold'); pdf.setTextColor(50,50,50);
  pdf.text('Total Pipes:', rightX, ry); pdf.text(`${ld.totalPipes}`, rightX + 30, ry);

  y = Math.max(y, ry) + 6;

  // Weight breakdown table
  hdr('Weight Breakdown');
  const wItems = [
    ['Standards', ld.weights.standards], ['Ledgers X', ld.weights.ledgersX], ['Ledgers Z', ld.weights.ledgersZ],
    ['Rosettes', ld.weights.rosettes], ['Base Jacks', ld.weights.baseJacks], ['Decks', ld.weights.decks],
  ];
  // Two-column weight table
  for (let i = 0; i < wItems.length; i += 2) {
    pdf.setFontSize(8); pdf.setFont('helvetica', 'normal'); pdf.setTextColor(90,90,90);
    pdf.text(wItems[i][0] + ':', leftX, y);
    pdf.setFont('helvetica', 'bold'); pdf.setTextColor(30,30,30);
    pdf.text(`${wItems[i][1].toFixed(1)} kg`, leftX + 30, y);
    if (wItems[i + 1]) {
      pdf.setFont('helvetica', 'normal'); pdf.setTextColor(90,90,90);
      pdf.text(wItems[i + 1][0] + ':', rightX, y);
      pdf.setFont('helvetica', 'bold'); pdf.setTextColor(30,30,30);
      pdf.text(`${wItems[i + 1][1].toFixed(1)} kg`, rightX + 30, y);
    }
    y += 4.5;
  }
  pdf.setDrawColor(160, 160, 160);
  pdf.line(leftX, y, leftX + 65, y);
  y += 3;
  pdf.setFontSize(9); pdf.setFont('helvetica', 'bold'); pdf.setTextColor(20,20,20);
  pdf.text(`Total Scaffold Weight: ${ld.weights.total.toFixed(1)} kg`, leftX, y);
  y += 8;

  // ===== Load Capacity (same page if space, otherwise new page) =====
  if (y > 200) { pdf.addPage(); y = m; }

  hdr('Load Capacity Analysis');
  row('Ultimate Top Load:', `${ld.topLoad.ultimate.toLocaleString()} kg`);
  row(`Safe Top Load (÷${ld.oshaFactor}):`, `${ld.topLoad.safe.toLocaleString()} kg`);
  row('Available Top Load:', `${ld.topLoad.available.toLocaleString()} kg`, [0, 120, 0]);
  sep(2);

  const topInput = li.topLoad || 0;
  if (topInput > 0) {
    const topSafe = topInput <= ld.topLoad.available;
    row('User Top Load:', `${topInput.toLocaleString()} kg → ${topSafe ? 'SAFE' : 'OVERLOAD'}`, topSafe ? [0, 130, 0] : [200, 0, 0]);
  }
  sep(3);

  // Side capacities in a compact table
  pdf.setFontSize(8.5); pdf.setFont('helvetica', 'bold'); pdf.setTextColor(50,50,50);
  pdf.text('Side Hanging Load Capacities:', leftX, y); y += 5;
  for (let i = 0; i < 4; i += 2) {
    const sides = ['front', 'back', 'left', 'right'];
    const s1 = sides[i], s2 = sides[i + 1];
    pdf.setFont('helvetica', 'normal'); pdf.setTextColor(90,90,90);
    pdf.text(`${s1.charAt(0).toUpperCase() + s1.slice(1)}:`, leftX + 2, y);
    pdf.setFont('helvetica', 'bold'); pdf.setTextColor(30,30,30);
    pdf.text(`${ld.sideCapacities[s1].toLocaleString()} kg`, leftX + 22, y);
    pdf.setFont('helvetica', 'normal'); pdf.setTextColor(90,90,90);
    pdf.text(`${s2.charAt(0).toUpperCase() + s2.slice(1)}:`, rightX, y);
    pdf.setFont('helvetica', 'bold'); pdf.setTextColor(30,30,30);
    pdf.text(`${ld.sideCapacities[s2].toLocaleString()} kg`, rightX + 22, y);
    y += 4.5;
  }

  const sideInput = li.sideLoad || 0;
  const sideLoadSides = li.sideLoadSides || {};
  const activeSides = Object.keys(sideLoadSides).filter(s => sideLoadSides[s]);
  if (sideInput > 0 && activeSides.length > 0) {
    sep(1);
    row('User Side Load:', `${sideInput.toLocaleString()} kg on ${activeSides.map(s => s.charAt(0).toUpperCase() + s.slice(1)).join(', ')}`);
    for (const side of activeSides) {
      const cap = ld.sideCapacities[side];
      const safe = sideInput <= cap;
      row(`  ${side.charAt(0).toUpperCase() + side.slice(1)}:`, safe ? 'SAFE' : 'OVERLOAD', safe ? [0, 130, 0] : [200, 0, 0]);
    }
  }
  sep(4);

  // Support & Counterweight (compact, same page)
  if (y > 240) { pdf.addPage(); y = m; }
  hdr('Support & Counterweight');
  let totalCW = 0;
  const allUnits = [scaffoldConfig, ...supports];
  for (const u of allUnits) totalCW += (u.counterweights?.length || 0);
  row('Support Type:', 'Ground Mounted');
  row('Counterweight Units:', `${totalCW} (${(totalCW * 1000).toLocaleString()} kg)`);

  // Safety box
  sep(3);
  pdf.setFillColor(255, 225, 225);
  pdf.roundedRect(m, y, cw, 8, 1.5, 1.5, 'F');
  pdf.setFontSize(6.5);
  pdf.setTextColor(180, 0, 0);
  pdf.setFont('helvetica', 'bold');
  pdf.text('IMPORTANT: All load calculations use OSHA 4:1 safety factor. Values are theoretical maximums. Must be verified by a structural engineer.', m + 3, y + 5);
  y += 12;

  // ===== VIEW IMAGES: 2 per page (3 pages for 6 views) =====
  const scaffoldNodes = scene.transformNodes.filter(n => n.metadata?.isScaffolding);
  let target = BABYLON.Vector3.Zero();
  let camDist = 15;
  if (scaffoldNodes.length > 0) {
    const node = scaffoldNodes[0];
    const children = node.getChildMeshes();
    if (children.length > 0) {
      let mn = children[0].getBoundingInfo().boundingBox.minimumWorld.clone();
      let mx = children[0].getBoundingInfo().boundingBox.maximumWorld.clone();
      for (let i = 1; i < children.length; i++) {
        if (!children[i].isVisible && children[i].visibility === 0) continue;
        const b = children[i].getBoundingInfo();
        mn = BABYLON.Vector3.Minimize(mn, b.boundingBox.minimumWorld);
        mx = BABYLON.Vector3.Maximize(mx, b.boundingBox.maximumWorld);
      }
      target = BABYLON.Vector3.Center(mn, mx);
      camDist = BABYLON.Vector3.Distance(mn, mx) * 1.2;
    }
  }

  // 2 views per page
  for (let vi = 0; vi < VIEWS.length; vi += 2) {
    pdf.addPage();
    for (let j = 0; j < 2 && vi + j < VIEWS.length; j++) {
      const idx = vi + j;
      const topY = m + j * 138; // two halves of page
      try {
        const imgData = await captureView(scene, engine, target, camDist, VIEWS[idx]);
        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(40, 40, 40);
        pdf.text(VIEWS[idx].name, m, topY + 5);
        pdf.addImage(imgData, 'PNG', m, topY + 8, cw, cw * 0.68);
      } catch (err) {
        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'bold');
        pdf.text(VIEWS[idx].name, m, topY + 5);
        pdf.setFontSize(8);
        pdf.setFont('helvetica', 'normal');
        pdf.text('Capture failed', m, topY + 15);
      }
    }
  }

  // Legend on last page (append to bottom or new page)
  pdf.addPage();
  y = m;
  hdr('Load Zone Legend');
  const legend = [
    { label: 'Safe (< 70%)', color: [0, 140, 0] },
    { label: 'Warning (70–100%)', color: [220, 190, 0] },
    { label: 'Overload (> 100%)', color: [200, 0, 0] },
    { label: 'No Load', color: [180, 180, 180] },
  ];
  // Inline legend
  let lx = m + 2;
  for (const item of legend) {
    pdf.setFillColor(...item.color);
    pdf.rect(lx, y - 2, 5, 4, 'F');
    pdf.setFontSize(8); pdf.setTextColor(50, 50, 50); pdf.setFont('helvetica', 'normal');
    pdf.text(item.label, lx + 7, y + 1);
    lx += 42;
  }
  y += 8;
  pdf.setFontSize(7.5); pdf.setTextColor(100, 100, 100);
  pdf.text('Deck levels → top load zones  |  Side overlays → lateral/hanging load zones  |  Base blocks → counterweights', m + 2, y);

  const fileName = `Scaffold_Report_${(projectName || 'Project').replace(/\s+/g, '_')}_${new Date().toISOString().slice(0, 10)}.pdf`;
  pdf.save(fileName);
  return fileName;
}
